import { useState, useEffect, useCallback } from "react";
import {
  Users,
  Shield,
  Crown,
  UserCheck,
  UserX,
  UserPlus,
  RefreshCw,
  ChevronDown,
  Search,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { supabase, requireOrganizationId, type OrgRole } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../components/ui/ToastModal";
import { useAuditLog } from "../hooks/useAuditLog";
import { InviteMemberDialog } from "../components/InviteMemberDialog";
import { logError } from "../lib/logError";

// ─── Types ───────────────────────────────────────────────────
interface TeamMember {
  id: string;
  user_id: string;
  role: OrgRole;
  joined_at: string;
  profile: {
    email: string | null;
    full_name: string | null;
    is_platform_admin: boolean;
    last_login_at: string | null;
  } | null;
}

const ROLE_CONFIG: Record<
  string,
  { label: string; color: string; bg: string; icon: typeof Shield }
> = {
  super_admin: {
    label: "Super Admin",
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.10)",
    icon: Crown,
  },
  admin: {
    label: "Admin",
    color: "#3b82f6",
    bg: "rgba(59,130,246,0.10)",
    icon: Shield,
  },
  member: {
    label: "Member",
    color: "#10b981",
    bg: "rgba(16,185,129,0.10)",
    icon: UserCheck,
  },
  viewer: {
    label: "Viewer",
    color: "#8b5cf6",
    bg: "rgba(139,92,246,0.10)",
    icon: UserCheck,
  },
};

// ─── Component ───────────────────────────────────────────────
export function TeamPage() {
  const { profile, user } = useAuth();
  const { addToast } = useToast();
  const auditLog = useAuditLog();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const orgId = requireOrganizationId();

  // ── Fetch members ────────────────────────────────────────
  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("organization_members")
        .select(
          `
          id,
          user_id,
          role,
          joined_at,
          profiles!organization_members_user_id_profiles_fkey (
            email,
            full_name,
            is_platform_admin,
            last_login_at
          )
        `
        )
        .eq("organization_id", orgId)
        .order("joined_at", { ascending: true });

      if (error) throw error;

      // Map the Supabase join result to our TeamMember shape
      const mapped: TeamMember[] = (data || []).map((row: Record<string, unknown>) => ({
        id: row.id as string,
        user_id: row.user_id as string,
        role: row.role as OrgRole,
        joined_at: row.joined_at as string,
        profile: row.profiles
          ? {
              email: (row.profiles as Record<string, unknown>).email as string | null,
              full_name: (row.profiles as Record<string, unknown>).full_name as string | null,
              is_platform_admin: (row.profiles as Record<string, unknown>).is_platform_admin as boolean,
              last_login_at: (row.profiles as Record<string, unknown>).last_login_at as string | null,
            }
          : null,
      }));

      setMembers(mapped);
    } catch (err) {
      logError("[Team] Error fetching members:", err);
      addToast({ type: "error", title: "Failed to load team members" });
    } finally {
      setLoading(false);
    }
  }, [orgId, addToast]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  // ── Role change ──────────────────────────────────────────
  const handleRoleChange = async (memberId: string, newRole: OrgRole) => {
    setChangingRole(memberId);
    try {
      const member = members.find((m) => m.id === memberId);
      const oldRole = member?.role;

      const { error } = await supabase
        .from("organization_members")
        .update({ role: newRole })
        .eq("id", memberId)
        .eq("organization_id", orgId);

      if (error) throw error;

      setMembers((prev) =>
        prev.map((m) => (m.id === memberId ? { ...m, role: newRole } : m))
      );
      addToast({ type: "success", title: `Role updated to ${newRole}` });

      // Audit log, non-blocking
      auditLog.log({
        action: "member.role_change",
        entity_type: "member",
        entity_id: memberId,
        metadata: {
          member_name: member?.profile?.full_name ?? "Unknown",
          old_role: oldRole,
          new_role: newRole,
        },
      });
    } catch (err) {
      logError("[Team] Error updating role:", err);
      addToast({ type: "error", title: "Failed to update role" });
    } finally {
      setChangingRole(null);
    }
  };

  // ── Remove member ────────────────────────────────────────
  const handleRemoveMember = async (memberId: string, memberName: string) => {
    if (
      !window.confirm(
        `Are you sure you want to remove ${memberName} from this organization?`
      )
    ) {
      return;
    }

    try {
      const member = members.find((m) => m.id === memberId);

      const { error } = await supabase
        .from("organization_members")
        .delete()
        .eq("id", memberId)
        .eq("organization_id", orgId);

      if (error) throw error;

      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      addToast({ type: "success", title: `${memberName} removed from organization` });

      // Audit log, non-blocking
      auditLog.log({
        action: "member.remove",
        entity_type: "member",
        entity_id: memberId,
        metadata: {
          member_name: memberName,
          member_role: member?.role ?? "unknown",
        },
      });
    } catch (err) {
      logError("[Team] Error removing member:", err);
      addToast({ type: "error", title: "Failed to remove member" });
    }
  };

  // ── Filter ───────────────────────────────────────────────
  const filteredMembers = members.filter((m) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.profile?.full_name?.toLowerCase().includes(q) ||
      m.profile?.email?.toLowerCase().includes(q) ||
      m.role.toLowerCase().includes(q)
    );
  });

  // ── Stats ────────────────────────────────────────────────
  const stats = {
    total: members.length,
    admins: members.filter((m) => m.role === "admin" || m.role === "super_admin")
      .length,
    recent: members.filter((m) => {
      const joined = new Date(m.joined_at);
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return joined > thirtyDaysAgo;
    }).length,
  };

  // ── Render ───────────────────────────────────────────────
  return (
    <div style={{ padding: "var(--spacing-xl)" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "var(--spacing-xl)",
          flexWrap: "wrap",
          gap: "var(--spacing-md)",
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-sm)",
              marginBottom: "var(--spacing-xs)",
            }}
          >
            <Shield
              size={24}
              style={{ color: "var(--color-primary)" }}
            />
            <h1
              style={{
                margin: 0,
                fontSize: "var(--font-size-2xl)",
                fontWeight: 700,
                color: "var(--text-primary)",
              }}
            >
              Team Management
            </h1>
          </div>
          <p
            style={{
              margin: 0,
              color: "var(--text-muted)",
              fontSize: "var(--font-size-sm)",
            }}
          >
            Manage organization members, roles, and access permissions.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
          <button
            onClick={fetchMembers}
            className="btn btn-outline"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-xs)",
            }}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => setShowInviteDialog(true)}
            className="btn btn-primary"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-xs)",
            }}
          >
            <UserPlus size={16} />
            Invite Member
          </button>
        </div>
      </div>

      {/* Platform Admin Badge */}
      {profile?.is_platform_admin && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-sm)",
            padding: "var(--spacing-sm) var(--spacing-md)",
            background:
              "linear-gradient(135deg, rgba(139,92,246,0.08), rgba(59,130,246,0.06))",
            border: "1px solid rgba(139,92,246,0.2)",
            borderRadius: "var(--border-radius-md)",
            marginBottom: "var(--spacing-lg)",
            fontSize: "var(--font-size-sm)",
            color: "#8b5cf6",
          }}
        >
          <Crown size={16} />
          <span style={{ fontWeight: 600 }}>Platform Administrator</span>
          <span style={{ color: "var(--text-muted)" }}>
            · You have full cross-organization management access
          </span>
        </div>
      )}

      {/* Stats Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "var(--spacing-md)",
          marginBottom: "var(--spacing-xl)",
        }}
      >
        <StatCard
          label="Total Members"
          value={stats.total}
          icon={<Users size={20} />}
          color="var(--color-primary)"
        />
        <StatCard
          label="Admins & Super Admins"
          value={stats.admins}
          icon={<Shield size={20} />}
          color="#f59e0b"
        />
        <StatCard
          label="Joined (30d)"
          value={stats.recent}
          icon={<UserCheck size={20} />}
          color="#10b981"
        />
      </div>

      {/* Search Bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing-sm)",
          marginBottom: "var(--spacing-lg)",
        }}
      >
        <div
          style={{
            position: "relative",
            flex: 1,
            maxWidth: 400,
          }}
        >
          <Search
            size={16}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--text-muted)",
            }}
          />
          <input
            type="text"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: "100%",
              paddingLeft: 36,
              height: 40,
              border: "1px solid var(--border-color)",
              borderRadius: "var(--border-radius-md)",
              background: "var(--bg-card)",
              color: "var(--text-primary)",
              fontSize: "var(--font-size-sm)",
            }}
          />
        </div>
      </div>

      {/* Members Table */}
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "var(--spacing-3xl)",
          }}
        >
          <Loader2
            size={32}
            style={{
              animation: "spin 1s linear infinite",
              color: "var(--color-primary)",
            }}
          />
        </div>
      ) : filteredMembers.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "var(--spacing-3xl)",
            color: "var(--text-muted)",
          }}
        >
          <Users size={48} style={{ opacity: 0.3, marginBottom: "var(--spacing-md)" }} />
          <p>
            {searchQuery
              ? "No members match your search."
              : "No members found in this organization."}
          </p>
        </div>
      ) : (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--border-radius-lg)",
            overflow: "hidden",
          }}
        >
          {/* Table Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 100px 120px 100px 120px",
              gap: "var(--spacing-md)",
              padding: "var(--spacing-md) var(--spacing-lg)",
              borderBottom: "1px solid var(--border-color)",
              background: "var(--bg-secondary)",
              fontSize: "var(--font-size-xs)",
              fontWeight: 600,
              color: "var(--text-muted)",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            <span>Member</span>
            <span style={{ textAlign: "center" }}>Role</span>
            <span style={{ textAlign: "center" }}>Joined</span>
            <span style={{ textAlign: "center" }}>Last Active</span>
            <span style={{ textAlign: "center" }}>Actions</span>
          </div>

          {/* Member Rows */}
          {filteredMembers.map((member) => {
            const roleConfig =
              ROLE_CONFIG[member.role] || ROLE_CONFIG.member;
            const RoleIcon = roleConfig.icon;
            const isCurrentUser = member.user_id === user?.id;
            const isMemberPlatformAdmin =
              member.profile?.is_platform_admin === true;

            return (
              <div
                key={member.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 100px 120px 100px 120px",
                  gap: "var(--spacing-md)",
                  padding: "var(--spacing-md) var(--spacing-lg)",
                  borderBottom: "1px solid var(--border-color)",
                  alignItems: "center",
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "var(--bg-secondary)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {/* Member Info */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--spacing-sm)",
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: roleConfig.bg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "var(--font-size-sm)",
                        fontWeight: 600,
                        color: roleConfig.color,
                      }}
                    >
                      {(
                        member.profile?.full_name ||
                        member.profile?.email ||
                        "?"
                      )[0].toUpperCase()}
                    </span>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "var(--spacing-xs)",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 500,
                          color: "var(--text-primary)",
                          fontSize: "var(--font-size-sm)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {member.profile?.full_name || "Unknown"}
                      </span>
                      {isMemberPlatformAdmin && (
                        <span
                          style={{
                            fontSize: "0.625rem",
                            background: "rgba(139,92,246,0.12)",
                            color: "#8b5cf6",
                            padding: "1px 6px",
                            borderRadius: 99,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Platform Admin
                        </span>
                      )}
                      {isCurrentUser && (
                        <span
                          style={{
                            fontSize: "0.625rem",
                            background: "rgba(59,130,246,0.12)",
                            color: "#3b82f6",
                            padding: "1px 6px",
                            borderRadius: 99,
                            fontWeight: 600,
                          }}
                        >
                          You
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: "var(--font-size-xs)",
                        color: "var(--text-muted)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      }}
                    >
                      {member.profile?.email || "No email"}
                    </span>
                  </div>
                </div>

                {/* Role Badge */}
                <div style={{ textAlign: "center" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "4px 10px",
                      borderRadius: 99,
                      fontSize: "var(--font-size-xs)",
                      fontWeight: 600,
                      background: roleConfig.bg,
                      color: roleConfig.color,
                    }}
                  >
                    <RoleIcon size={12} />
                    {roleConfig.label}
                  </span>
                </div>

                {/* Joined Date */}
                <span
                  style={{
                    fontSize: "var(--font-size-xs)",
                    color: "var(--text-muted)",
                    textAlign: "center",
                  }}
                >
                  {new Date(member.joined_at).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>

                {/* Last Active */}
                <span
                  style={{
                    fontSize: "var(--font-size-xs)",
                    color: "var(--text-muted)",
                    textAlign: "center",
                  }}
                >
                  {member.profile?.last_login_at
                    ? new Date(member.profile.last_login_at).toLocaleDateString(
                        "en-AU",
                        { day: "numeric", month: "short" }
                      )
                    : "Never"}
                </span>

                {/* Actions */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "var(--spacing-xs)",
                  }}
                >
                  {/* Role Dropdown */}
                  {!isMemberPlatformAdmin && (
                    <div style={{ position: "relative" }}>
                      <select
                        value={member.role}
                        onChange={(e) =>
                          handleRoleChange(
                            member.id,
                            e.target.value as OrgRole
                          )
                        }
                        disabled={changingRole === member.id || isCurrentUser}
                        style={{
                          appearance: "none",
                          padding: "4px 28px 4px 8px",
                          border: "1px solid var(--border-color)",
                          borderRadius: "var(--border-radius-sm)",
                          background: "var(--bg-card)",
                          color: "var(--text-primary)",
                          fontSize: "var(--font-size-xs)",
                          cursor: isCurrentUser ? "not-allowed" : "pointer",
                          opacity: isCurrentUser ? 0.5 : 1,
                        }}
                      >
                        <option value="super_admin">Super Admin</option>
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      <ChevronDown
                        size={12}
                        style={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          pointerEvents: "none",
                          color: "var(--text-muted)",
                        }}
                      />
                    </div>
                  )}

                  {/* Remove Button */}
                  {!isCurrentUser && !isMemberPlatformAdmin && (
                    <button
                      onClick={() =>
                        handleRemoveMember(
                          member.id,
                          member.profile?.full_name || "this member"
                        )
                      }
                      title="Remove member"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        border: "1px solid var(--border-color)",
                        borderRadius: "var(--border-radius-sm)",
                        background: "transparent",
                        cursor: "pointer",
                        color: "var(--text-muted)",
                        transition: "all 0.15s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#ef4444";
                        e.currentTarget.style.color = "#ef4444";
                        e.currentTarget.style.background =
                          "rgba(239,68,68,0.06)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "var(--border-color)";
                        e.currentTarget.style.color = "var(--text-muted)";
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <UserX size={14} />
                    </button>
                  )}

                  {/* Loading indicator for role change */}
                  {changingRole === member.id && (
                    <Loader2
                      size={14}
                      style={{
                        animation: "spin 1s linear infinite",
                        color: "var(--color-primary)",
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Security Notice */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--spacing-sm)",
          marginTop: "var(--spacing-xl)",
          padding: "var(--spacing-md)",
          background: "rgba(245,158,11,0.06)",
          border: "1px solid rgba(245,158,11,0.15)",
          borderRadius: "var(--border-radius-md)",
          fontSize: "var(--font-size-xs)",
          color: "var(--text-muted)",
        }}
      >
        <AlertTriangle
          size={16}
          style={{ color: "#f59e0b", flexShrink: 0, marginTop: 1 }}
        />
        <div>
          <p style={{ margin: 0, fontWeight: 600, color: "#f59e0b" }}>
            Security Notice
          </p>
          <p style={{ margin: "2px 0 0" }}>
            Team management is restricted to platform administrators. All role
            changes and member removals are logged for audit purposes. Changes
            take effect immediately.
          </p>
        </div>
      </div>

      {/* Invite Member Dialog */}
      <InviteMemberDialog
        isOpen={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        onInvited={fetchMembers}
      />
    </div>
  );
}

// ── Stat Card ──────────────────────────────────────────────
function StatCard({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <div
      style={{
        padding: "var(--spacing-lg)",
        background: "var(--bg-card)",
        border: "1px solid var(--border-color)",
        borderRadius: "var(--border-radius-lg)",
        display: "flex",
        alignItems: "center",
        gap: "var(--spacing-md)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: "var(--border-radius-md)",
          background: `${color}14`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
        }}
      >
        {icon}
      </div>
      <div>
        <div
          style={{
            fontSize: "var(--font-size-2xl)",
            fontWeight: 700,
            color: "var(--text-primary)",
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        <div
          style={{
            fontSize: "var(--font-size-xs)",
            color: "var(--text-muted)",
            marginTop: 2,
          }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}
